import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface RatePlan {
  id: string
  name: string
  description: string
  tier: string
  limits: {
    requests_per_minute: number
    requests_per_hour: number
    requests_per_day: number
    requests_per_month: number
    burst_limit: number
    max_payload_mb: number
    concurrent_connections: number
  }
  features: string[]
  price_monthly: number
  overage_price_per_1k: number
  active: boolean
}

interface CustomerPlan {
  id: string
  customer_id: string
  customer_name: string
  plan_id: string
  status: string
  usage_this_period: {
    requests: number
    bandwidth_gb: number
    avg_response_time_ms: number
  }
}

interface RateLimitEvent {
  id: string
  customer_id: string
  timestamp: string
  limit_type: string
  limit_value: number
  current_value: number
  action: string
  endpoint: string
}

export default function RatePlansPage() {
  const [plans, setPlans] = useState<RatePlan[]>([])
  const [customerPlans, setCustomerPlans] = useState<CustomerPlan[]>([])
  const [events, setEvents] = useState<RateLimitEvent[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [planRes, custRes, eventRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/rate-plans/plans'),
      fetch(API_BASE + '/rate-plans/customer-plans'),
      fetch(API_BASE + '/rate-plans/events'),
      fetch(API_BASE + '/rate-plans/statistics')
    ])
    if (planRes.ok) setPlans((await planRes.json()).plans || [])
    if (custRes.ok) setCustomerPlans((await custRes.json()).customer_plans || [])
    if (eventRes.ok) setEvents((await eventRes.json()).events || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'rate_limited': return 'bg-red-100 text-red-800'
      case 'suspended': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatLimit = (limit: number) => {
    if (limit === -1) return 'Unlimited'
    if (limit >= 1000000) return `${(limit / 1000000).toFixed(0)}M`
    if (limit >= 1000) return `${(limit / 1000).toFixed(0)}K`
    return limit.toString()
  }

  const getPlanName = (planId: string) => {
    return plans.find(p => p.id === planId)?.name || planId
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">API Rate Plan Management</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Rate Plans</div>
              <div className="text-2xl font-bold">{statistics.total_plans}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Customers</div>
              <div className="text-2xl font-bold">{statistics.total_customers}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_customers}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Rate Limited</div>
              <div className="text-2xl font-bold text-red-600">{statistics.rate_limited_customers}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Requests (Month)</div>
              <div className="text-2xl font-bold text-blue-600">{formatLimit(statistics.total_requests_this_month)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Events Today</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.rate_limit_events_today}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {plans.map(plan => (
            <div key={plan.id} className={`bg-white rounded-lg shadow p-6 ${plan.tier === 'professional' ? 'border-2 border-blue-500' : ''}`}>
              {plan.tier === 'professional' && <div className="text-blue-600 text-sm font-semibold mb-2">Most Popular</div>}
              <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
              <p className="text-sm text-gray-500 mb-4">{plan.description}</p>
              <div className="text-3xl font-bold mb-4">
                ${plan.price_monthly}<span className="text-sm font-normal text-gray-500">/mo</span>
              </div>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">Requests/min</span>
                  <span className="font-medium">{formatLimit(plan.limits.requests_per_minute)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Requests/month</span>
                  <span className="font-medium">{formatLimit(plan.limits.requests_per_month)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Burst limit</span>
                  <span className="font-medium">{formatLimit(plan.limits.burst_limit)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Connections</span>
                  <span className="font-medium">{formatLimit(plan.limits.concurrent_connections)}</span>
                </div>
              </div>
              {plan.overage_price_per_1k > 0 && (
                <div className="text-xs text-gray-500">Overage: ${plan.overage_price_per_1k}/1K requests</div>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Customer Plans</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requests</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Response</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {customerPlans.map(cp => (
                  <tr key={cp.id}>
                    <td className="px-6 py-4 font-medium">{cp.customer_name}</td>
                    <td className="px-6 py-4">{getPlanName(cp.plan_id)}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(cp.status)}`}>{cp.status.replace(/_/g, ' ')}</span></td>
                    <td className="px-6 py-4">{cp.usage_this_period.requests.toLocaleString()}</td>
                    <td className="px-6 py-4">{cp.usage_this_period.avg_response_time_ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Rate Limit Events</h3>
            <div className="space-y-3">
              {events.slice(0, 6).map(event => (
                <div key={event.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{event.limit_type.replace(/_/g, ' ')}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${event.action === 'blocked' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{event.action}</span>
                  </div>
                  <div className="text-xs text-gray-600">{event.endpoint}</div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{event.current_value}/{event.limit_value}</span>
                    <span>{new Date(event.timestamp).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
